import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setCurrentStep,
  addCompletedStep,
  setValidationAttempted,
  setSelectedTask,
  clearSelectedTask,
  updateDocumentationData,
  setCROutput,
  setActiveTab,
  resetCreateCR,
  updateCustomerInfo,
  hydrateFromSavedCR,
  updateGeneralConfigurationData,
  setBomType,
  WorkflowStep
} from '../../../store/slices/createCRSlice';
import { lenderPackagesService } from '../../../services/lender-packages.service';
import { BomType, BOM_TYPES } from '../../../constants/lender-packages.constants';
import {
  selectCurrentStep,
  selectCompletedSteps,
  selectTaskData,
  selectDocumentationData,
  selectCROutput,
  selectActiveTab,
  selectIsTaskValid,
  selectIsModuleValid,
  selectIsInverterValid,
  selectIsOptimizerValid,
  selectAuroraPanelRowsPool,
  selectConfiguredSystems,
  selectAllSystemsModuleData,
  selectStructure1Data,
  selectStructure2Data,
  selectStructure3Data,
  selectStructuresRoofsDetails,
  selectGeneralConfigurationData,
  selectLenderPackageItems,
  selectIsLgcyClient,
} from '../../../store/selectors/createCRSelectors';
import { ROUTES } from '../../../constants';
import { CR_ATTRIBUTE_ORDER, MOUNTING_DEFAULTS } from '../../../constants/app.constants';
import { constructionReviewsService, ConstructionReview } from '../../../services/construction-reviews.service';
import { useCrAutoSave } from '../../../hooks';
import { extractCustomerInfoFromTask, extractClientFromTask } from '../../../utils';
import { Button, ConfirmationModal, ConfirmationType } from '../../../components';

// Import all the components
import { SimpleTaskSelector } from './steps/JobSelection/SimpleTaskSelector';
import { EquipmentStep } from './steps/Equipment/EquipmentStep';
import { ElectricalStep } from './steps/Electrical/ElectricalStep';
import { OutputPreview } from './OutputPreview';
import { MountingStep } from './steps/Mounting/MountingStep';
import { BOMStep } from './steps/BOM/BOMStep';
import { DocumentationStep } from './steps/Documentation/DocumentationStep';

const ALL_WORKFLOW_STEPS = [
  { id: WorkflowStep.TASK, label: 'Job Setup', required: true },
  { id: WorkflowStep.EQUIPMENT, label: 'Equipment', required: true },
  { id: WorkflowStep.ELECTRICAL, label: 'Electrical', required: false },
  { id: WorkflowStep.MOUNTING, label: 'Mounting', required: false },
  { id: WorkflowStep.BOM, label: 'BOM', required: false },
  { id: WorkflowStep.DOCUMENTATION, label: 'Documentation', required: false },
  { id: WorkflowStep.OUTPUT, label: 'Output Review', required: false }
];

const STEP_ICONS: Record<WorkflowStep, React.ReactElement> = {
  [WorkflowStep.TASK]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7h-5m5 0a2 2 0 01-2 2h-3m5-2a2 2 0 00-2-2h-3m-4 0H6a2 2 0 00-2 2v9a2 2 0 002 2h8a2 2 0 002-2V7m-4-2v4m0 0H8m4 0h4" />
  ),
  [WorkflowStep.EQUIPMENT]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M4 9h16M5 21h14a2 2 0 002-2v-7H3v7a2 2 0 002 2z" />
  ),
  [WorkflowStep.ELECTRICAL]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  [WorkflowStep.MOUNTING]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7l8-4 8 4-8 4-8-4zm0 8l8 4 8-4" />
  ),
  [WorkflowStep.BOM]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  ),
  [WorkflowStep.DOCUMENTATION]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  ),
  [WorkflowStep.OUTPUT]: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2-10l-3-3m0 0L9 6m5-3v12" />
  )
};

export const WorkflowPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlCRId } = useParams<{ id: string }>();
  const isResuming = !!urlCRId || !!(location.state as { resumeCRId?: string })?.resumeCRId;
  
  // Redux selectors
  const currentStep = useAppSelector(selectCurrentStep);
  const completedSteps = useAppSelector(selectCompletedSteps);
  const taskData = useAppSelector(selectTaskData);
  const documentationData = useAppSelector(selectDocumentationData);
  const crOutput = useAppSelector(selectCROutput);
  const activeTab = useAppSelector(selectActiveTab);
  
  // Fire-and-forget auto-save: upserts the CR record in the DB on each Next click
  const { autoSave: performAutoSave } = useCrAutoSave();

  // State for save confirmation
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [mountingWarning, setMountingWarning] = useState<React.ReactNode | null>(null);
  const [bomWarning, setBomWarning] = useState<React.ReactNode | null>(null);
  const [existingCR, setExistingCR] = useState<ConstructionReview | null>(null);
  const [hydratedFromExisting, setHydratedFromExisting] = useState(false);

  const WORKFLOW_STEPS = ALL_WORKFLOW_STEPS;

  // Validation selectors
  const isTaskValid = useAppSelector(selectIsTaskValid);
  const isModuleValid = useAppSelector(selectIsModuleValid);
  const isInverterValid = useAppSelector(selectIsInverterValid);
  const isOptimizerValid = useAppSelector(selectIsOptimizerValid);

  // Mounting validation selectors
  const auroraPanelRowsPool = useAppSelector(selectAuroraPanelRowsPool);
  const configuredSystems = useAppSelector(selectConfiguredSystems);
  const allSystemsModuleData = useAppSelector(selectAllSystemsModuleData);
  const structure1Data = useAppSelector(selectStructure1Data);
  const structure2Data = useAppSelector(selectStructure2Data);
  const structure3Data = useAppSelector(selectStructure3Data);
  const structuresRoofsDetails = useAppSelector(selectStructuresRoofsDetails);
  const generalConfigData = useAppSelector(selectGeneralConfigurationData);


  // BOM step: selectors for zero-quantity warning
  const lenderPackageItems = useAppSelector(selectLenderPackageItems);
  const isLgcyClient = useAppSelector(selectIsLgcyClient);

  /** Check if any enabled lender package bom items have quantity 0 */
  const getBomWarning = (): React.ReactNode | null => {
    if (!isLgcyClient || lenderPackageItems.length === 0) return null;
    const zeroQtyItems: { type: string; label: string }[] = [];
    for (const item of lenderPackageItems) {
      for (const bom of item.bom_items) {
        if (bom.quantity === 0) {
          zeroQtyItems.push({
            type: item.item_type,
            label: `${bom.bom_kind} (${bom.bom_sku || bom.bom_mpn})`,
          });
        }
      }
    }
    if (zeroQtyItems.length === 0) return null;
    return (
      <div className="space-y-2.5">
        <p className="text-sm text-slate-600">The following enabled package items have quantity 0:</p>
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {zeroQtyItems.map((item, i) => (
            <li key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-amber-50/70 border border-amber-200/50 rounded-lg text-xs">
              <span className="font-semibold text-amber-700 uppercase shrink-0">{item.type}</span>
              <span className="text-slate-600 truncate">{item.label}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-amber-500">Do you want to proceed anyway?</p>
      </div>
    );
  };

  // Mounting step warning: check for unassigned panel rows
  const getMountingWarning = (): React.ReactNode | null => {
    const structuresData = [structure1Data, structure2Data, structure3Data];

    // Case 1: Aurora pool exists — warn about unassigned rows + structures without panel rows
    if (auroraPanelRowsPool !== null) {
      // Pool is the immutable master set; derive unassigned by subtracting rows already on structures
      const assignedRowIds = new Set<string>();
      structuresData.forEach(sd => {
        (sd?.attachments_rails?.panel_rows_1?.rows || []).forEach((r: Record<string, unknown>) => assignedRowIds.add(r.id as string));
        (sd?.attachments_rails?.panel_rows_2?.rows || []).forEach((r: Record<string, unknown>) => assignedRowIds.add(r.id as string));
      });
      const unassignedRows = auroraPanelRowsPool.filter(r => !assignedRowIds.has(r.id));
      const unassignedCount = unassignedRows.length;
      const emptyAuroraStructures: string[] = [];
      structuresData.forEach((sd, sIdx) => {
        const hasName = sd?.name;
        if (!hasName) return;
        const rowCount = (sd?.attachments_rails?.panel_rows_1?.rows?.length || 0) +
                         (sd?.attachments_rails?.panel_rows_2?.rows?.length || 0);
        if (rowCount === 0) emptyAuroraStructures.push(sd.name || `Structure ${sIdx + 1}`);
      });

      if (unassignedCount > 0 || emptyAuroraStructures.length > 0) {
        const totalPanels = unassignedRows.reduce((s, r) => s + r.panel_count, 0);
        const unassignedOffset = unassignedCount > 0 ? 1 : 0;
        return (
          <div className="space-y-2.5">
            {unassignedCount > 0 && (
              <div className="flex items-center gap-2.5 bg-amber-50/70 border border-amber-200/50 rounded-lg px-3 py-2.5">
                <span className="shrink-0 w-5 h-5 rounded-full bg-amber-200/60 text-amber-700 text-[10px] font-bold flex items-center justify-center">1</span>
                <span className="font-medium text-slate-700">
                  <span className="font-semibold text-amber-700">{unassignedCount} unassigned</span> Aurora panel row{unassignedCount > 1 ? 's' : ''} ({totalPanels} panels)
                </span>
              </div>
            )}
            {emptyAuroraStructures.map((name, i) => (
              <div key={name} className="flex items-center gap-2.5 bg-amber-50/70 border border-amber-200/50 rounded-lg px-3 py-2.5">
                <span className="shrink-0 w-5 h-5 rounded-full bg-amber-200/60 text-amber-700 text-[10px] font-bold flex items-center justify-center">{i + 1 + unassignedOffset}</span>
                <span className="font-medium text-amber-700">{name}</span>
                <span className="text-amber-400 ml-auto shrink-0">no panel rows</span>
              </div>
            ))}
            <p className="text-[11px] text-amber-500">Check Attachments & Rails in your Structures.</p>
          </div>
        );
      }
      return null;
    }

    // Case 2: No Aurora — warn if per-system panel counts don't match module quantities
    const mismatches: { moduleName: string; total: number; assigned: number }[] = [];
    const emptyStructures: string[] = [];

    configuredSystems.forEach(sk => {
      const systemQty = allSystemsModuleData[sk]?.quantity || 0;
      if (systemQty === 0) return;
      let assigned = 0;
      structuresData.forEach(sd => {
        [...(sd?.attachments_rails?.panel_rows_1?.rows || []),
         ...(sd?.attachments_rails?.panel_rows_2?.rows || [])].forEach((r: unknown) => {
          const row = r as { system_key?: string; panel_count?: number };
          if ((row.system_key || 'system_one') === sk) assigned += (row.panel_count || 0);
        });
      });
      if (assigned !== systemQty) {
        mismatches.push({
          moduleName: allSystemsModuleData[sk]?.moduleName || sk,
          total: systemQty,
          assigned
        });
      }
    });

    structuresData.forEach((sd, sIdx) => {
      const hasData = sd && Object.keys(sd).length > 0 && (sd.roof_configuration || sd.attachments_rails);
      if (!hasData) return;
      const rowCount = (sd?.attachments_rails?.panel_rows_1?.rows?.length || 0) +
                       (sd?.attachments_rails?.panel_rows_2?.rows?.length || 0);
      if (rowCount === 0) emptyStructures.push(`Structure ${sIdx + 1}`);
    });

    if (mismatches.length > 0) {
      return (
        <div className="space-y-2.5">
          {mismatches.map((m, i) => (
            <div key={i} className="flex items-center gap-2.5 bg-amber-50/70 border border-amber-200/50 rounded-lg px-3 py-2.5">
              <span className="shrink-0 w-5 h-5 rounded-full bg-amber-200/60 text-amber-700 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <span className="font-medium text-slate-700 truncate mr-auto">{m.moduleName}</span>
              <span className="shrink-0 text-right">
                <span className="font-bold text-amber-600">{m.total - m.assigned}</span>
                <span className="text-slate-400"> / {m.total} unused</span>
              </span>
            </div>
          ))}
          {emptyStructures.length > 0 && (
            <div className="flex items-center gap-2.5 bg-amber-50/70 border border-amber-200/50 rounded-lg px-3 py-2.5 text-amber-700 font-medium">
              <span className="shrink-0 w-5 h-5 rounded-full bg-amber-200/60 text-amber-700 text-[10px] font-bold flex items-center justify-center">{mismatches.length + 1}</span>
              {emptyStructures.join(', ')} {emptyStructures.length === 1 ? 'has' : 'have'} no panel rows configured.
            </div>
          )}
          <p className="text-[11px] text-amber-500">Check panel rows in your structures.</p>
        </div>
      );
    }
    return null;
  };

  // Validation logic
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case WorkflowStep.TASK:
        return isTaskValid;
      case WorkflowStep.EQUIPMENT:
        return isModuleValid && isInverterValid && isOptimizerValid;
      default:
        return true;
    }
  };
  
  // Equipment validation for enabling step 6 (output)
  const isEquipmentComplete = isModuleValid && isInverterValid && isOptimizerValid;
  
  const isStepClickable = (stepId: WorkflowStep) => {
    const stepIndex = WORKFLOW_STEPS.findIndex(s => s.id === stepId);
    const currentIndex = WORKFLOW_STEPS.findIndex(s => s.id === currentStep);
    
    if (stepIndex === currentIndex) return true;
    if (stepIndex < currentIndex) return true;
    
    // Once steps 1 (task) and 2 (equipment) are valid, enable steps 3 and 4
    const areBasicStepsValid = isTaskValid && isEquipmentComplete;
    if (areBasicStepsValid && [WorkflowStep.ELECTRICAL, WorkflowStep.MOUNTING].includes(stepId)) {
      return true;
    }

    // Steps 5, 6, and 7 require Mounting to have been confirmed (Next clicked)
    const isMountingCompleted = completedSteps.includes(WorkflowStep.MOUNTING);
    if (areBasicStepsValid && isMountingCompleted && [WorkflowStep.BOM, WorkflowStep.DOCUMENTATION, WorkflowStep.OUTPUT].includes(stepId)) {
      return true;
    }
    
    // Check if all previous required steps are completed
    for (let i = 0; i < stepIndex; i++) {
      const step = WORKFLOW_STEPS[i];
      if (step.required && !completedSteps.includes(step.id)) {
        return false;
      }
    }
    
    if (stepIndex <= 0) return true;
    return completedSteps.includes(WORKFLOW_STEPS[stepIndex - 1].id);
  };
  
  const applyMountingDefaults = () => {
    if (!generalConfigData.mount_system) {
      dispatch(updateGeneralConfigurationData({
        mount_system: generalConfigData.mount_system || MOUNTING_DEFAULTS.MOUNT_SYSTEM,
        roof_mount_conduit_size: generalConfigData.roof_mount_conduit_size || MOUNTING_DEFAULTS.ROOF_CONDUIT_SIZE,
        roof_mount_conduit_type: generalConfigData.roof_mount_conduit_type || MOUNTING_DEFAULTS.ROOF_CONDUIT_TYPE,
      }));
    }
  };

  const handleStepClick = (stepId: WorkflowStep) => {
    if (isStepClickable(stepId)) {
      if (stepId === WorkflowStep.MOUNTING) {
        applyMountingDefaults();
      }
      dispatch(setCurrentStep(stepId));
    }
  };
  
  const handleNextStep = () => {
    dispatch(setValidationAttempted({ step: currentStep, attempted: true }));

    if (!isCurrentStepValid()) {
      if (currentStep === WorkflowStep.TASK) {
        if (!taskData.selected_task) {
          toast.error('Please select a job before proceeding');
        }
      }
      if (currentStep === WorkflowStep.EQUIPMENT) {
        return;
      }
      return;
    }

    // Mounting step: warn about unassigned panel rows
    if (currentStep === WorkflowStep.MOUNTING) {
      const warning = getMountingWarning();
      if (warning) {
        setMountingWarning(warning);
        return;
      }
    }

    // BOM step: warn about enabled items with quantity 0
    if (currentStep === WorkflowStep.BOM) {
      const warning = getBomWarning();
      if (warning) {
        setBomWarning(warning);
        return;
      }
    }

    const currentIndex = WORKFLOW_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex < WORKFLOW_STEPS.length - 1) {
      const nextStep = WORKFLOW_STEPS[currentIndex + 1];
      if (nextStep.id === WorkflowStep.MOUNTING) {
        applyMountingDefaults();
      }
      dispatch(addCompletedStep(currentStep));
      dispatch(setCurrentStep(nextStep.id));
      performAutoSave();
    }
  };

  const handleMountingWarningProceed = () => {
    setMountingWarning(null);
    const currentIndex = WORKFLOW_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex < WORKFLOW_STEPS.length - 1) {
      const nextStep = WORKFLOW_STEPS[currentIndex + 1];
      dispatch(addCompletedStep(currentStep));
      dispatch(setCurrentStep(nextStep.id));
      performAutoSave();
    }
  };

  const handleBomWarningProceed = () => {
    setBomWarning(null);
    const currentIndex = WORKFLOW_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex < WORKFLOW_STEPS.length - 1) {
      const nextStep = WORKFLOW_STEPS[currentIndex + 1];
      dispatch(addCompletedStep(currentStep));
      dispatch(setCurrentStep(nextStep.id));
      performAutoSave();
    }
  };
  
  const handlePreviousStep = () => {
    const currentIndex = WORKFLOW_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      const previousStep = WORKFLOW_STEPS[currentIndex - 1];
      dispatch(setCurrentStep(previousStep.id));
    }
  };
  
  const handleTaskSelect = async (task: any) => {
    if (!task) {
      dispatch(clearSelectedTask());
      return;
    }

    // Skip existing CR check when resuming — we already know it exists
    if (!isResuming) {
      const existing = await constructionReviewsService.getLatestByTaskId(task.gid);
      if (existing) {
        setExistingCR(existing);
        return;
      }
    }

    dispatch(updateCustomerInfo(extractCustomerInfoFromTask(task)));
    const clientStr = extractClientFromTask(task);

    let resolvedClientId: string | undefined;
    let resolvedClientName: string | undefined;
    try {
      const clients = await lenderPackagesService.getClients();
      const match = clients.find(c => c.name.toUpperCase().includes((clientStr || '').toUpperCase()));
      resolvedClientId = match?.id;
      resolvedClientName = match?.name;
    } catch {
      // Non-critical — client will remain unresolved; user can still proceed
    }

    const defaultBomType: BomType = (clientStr || '').toUpperCase().includes('LGCY') ? BOM_TYPES.LGCY : BOM_TYPES.STANDARD;
    dispatch(setSelectedTask({
      gid: task.gid,
      name: task.name,
      client: clientStr || undefined,
      client_id: resolvedClientId,
      client_name: resolvedClientName,
    }));
    dispatch(setBomType(defaultBomType));
  };

  const handleViewExistingCR = () => {
    if (existingCR) {
      navigate(`/review/${existingCR.id}`);
    }
    setExistingCR(null);
  };

  const handleProceedWithExistingCR = () => {
    if (existingCR) {
      dispatch(hydrateFromSavedCR({
        task_id: existingCR.task_id,
        task_name: existingCR.task_name,
        structured_data: existingCR.structured_data || {},
        aurora_full_data: existingCR.aurora_full_data || null,
        cr_output: {
          generated_cr: existingCR.generated_cr || {},
          updated_cr: existingCR.updated_cr || {},
          changes: existingCR.changes || {},
        },
      }));
      setHydratedFromExisting(true);
    }
    setExistingCR(null);
  };
  
  
  const handleSaveComplete = () => {
    dispatch(resetCreateCR());
    navigate(ROUTES.HOME);
  };

  // Handle copy to clipboard
  const handleCopy = async () => {
    if (!crOutput?.updated_cr || Object.keys(crOutput.updated_cr).length === 0) {
      toast.error('No data to copy');
      return;
    }

    // Apply CR_ATTRIBUTE_ORDER for consistent ordering
    const orderedEntries: Array<[string, any]> = [];
    const processedKeys = new Set<string>();
    
    // First, add entries in the predefined order
    CR_ATTRIBUTE_ORDER.forEach(key => {
      if (crOutput.updated_cr.hasOwnProperty(key)) {
        orderedEntries.push([key, crOutput.updated_cr[key]]);
        processedKeys.add(key);
      }
    });
    
    // Then, add any remaining entries that weren't in the predefined order
    Object.entries(crOutput.updated_cr).forEach(([key, value]) => {
      if (!processedKeys.has(key)) {
        orderedEntries.push([key, value]);
      }
    });

    const editableText = orderedEntries
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(editableText);
      toast.success('Editable output copied to clipboard!', {
        duration: 2000,
        icon: '📋'
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Handle save click
  const handleSaveClick = () => {
    if (!taskData.selected_task) {
      toast.error('Please select a task first');
      return;
    }
    setShowSaveConfirmation(true);
  };

  // Function to save CR draft to database
  const handleSaveDraft = async () => {
    setShowSaveConfirmation(false);

    if (!taskData.selected_task) {
      toast.error('Please select a task first');
      return;
    }

    try {
      // Apply CR_ATTRIBUTE_ORDER to ensure proper ordering for database storage
      const applyAttributeOrder = (data: Record<string, any>): Record<string, any> => {
        const orderedData: Record<string, any> = {};
        const processedKeys = new Set<string>();
        
        // First, add entries in the predefined order
        CR_ATTRIBUTE_ORDER.forEach(key => {
          if (data.hasOwnProperty(key)) {
            orderedData[key] = data[key];
            processedKeys.add(key);
          }
        });
        
        // Then, add any remaining entries that weren't in the predefined order
        Object.entries(data).forEach(([key, value]) => {
          if (!processedKeys.has(key)) {
            orderedData[key] = value;
          }
        });
        
        return orderedData;
      };

      const crPayload = {
        task_id: taskData.selected_task.gid,
        task_name: taskData.selected_task.name,
        generated_cr: applyAttributeOrder(crOutput?.generated_cr || {}),
        updated_cr: applyAttributeOrder(crOutput?.updated_cr || {}),
        changes: crOutput?.changes || {}
      };

      await constructionReviewsService.create(crPayload);
      toast.success('Construction review saved successfully!', {
        duration: 3000,
        icon: '✅'
      });
      
      // Call the callback to reset form and go to task selection
      handleSaveComplete();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmation(false);
  };
  
  const isModuleSelectorEnabled = !!taskData.selected_task;
  const primaryButtonBase = 'px-4 py-2 rounded-full text-xs 3xl:text-sm font-medium transition-all duration-200 flex items-center gap-1.5';
  const primaryButtonEnabled = 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-sm shadow-emerald-500/20';
  const primaryButtonDisabled = 'bg-slate-100 text-slate-500 hover:bg-slate-200';
  const secondaryButtonClass = 'px-4 py-2 rounded-full text-xs 3xl:text-sm font-medium transition-all duration-200 bg-white text-slate-700 hover:bg-slate-100 flex items-center gap-1.5 border border-slate-200';
  
  return (
    <div className="h-[calc(100vh-64px)] overflow-hidden">
      <div className="w-full mx-auto h-full flex flex-col">
      <div className="flex-shrink-0 mx-[9%] my-4">
        <div className="glass-panel rounded-2xl px-4 py-2">
          <div className="flex flex-col gap-1">
            <div className="relative flex items-center overflow-x-auto scrollbar-hide px-1">
                {WORKFLOW_STEPS.map((step, index) => {
                  const isActive = step.id === currentStep;
                  const isCompleted = completedSteps.includes(step.id);
                  const isClickable = isStepClickable(step.id);

                  return (
                    <React.Fragment key={step.id}>
                      <button
                        onClick={() => handleStepClick(step.id)}
                        disabled={!isClickable}
                        className={`
                          group flex items-center gap-2 rounded-xl px-3 py-1.5 text-left transition-all duration-200 shrink-0 flex-1 justify-center
                          ${isActive ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20 border border-transparent' : ''}
                          ${isCompleted && !isActive ? 'bg-slate-100/80 text-slate-700 border border-slate-200/70' : ''}
                          ${!isActive && !isCompleted ? 'bg-white/70 text-slate-600 border border-slate-200/70 hover:border-slate-300' : ''}
                          ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
                        `}
                      >
                        <span className={`flex h-6 w-6 items-center justify-center rounded-lg text-[10px] 3xl:text-[11px] font-bold ${isActive ? 'bg-white/15 text-white' : isCompleted ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' : 'bg-white text-slate-500 border border-slate-300'}`}>
                          {isCompleted && !isActive ? (
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            index + 1
                          )}
                        </span>
                        <p className={`text-[11px] 3xl:text-xs font-semibold whitespace-nowrap ${isActive ? 'text-white' : isCompleted ? 'text-slate-700' : 'text-slate-700'}`}>
                          {step.label}
                        </p>
                      </button>
                      {index < WORKFLOW_STEPS.length - 1 && (
                        <div className={`h-0.5 flex-1 mx-1.5 rounded-full transition-all duration-300 ${isCompleted ? 'bg-blue-300' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content area */}
      {currentStep === WorkflowStep.BOM ? (
        /* BOM step: full-width layout (no CR output panel) */
        <div className="flex-1 mx-[9%] pb-4 min-h-0 flex flex-col">
          <div className="glass-panel rounded-3xl h-full flex flex-col min-h-0 border border-teal-100/60">
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <BOMStep isEnabled={!!taskData.selected_task} />
            </div>
            <div className="flex-shrink-0 border-t border-teal-100/60 px-6 py-4 bg-white/70 rounded-b-3xl">
              <div className="flex justify-between">
                <button onClick={handlePreviousStep} className={secondaryButtonClass}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <button onClick={handleNextStep} className={`${primaryButtonBase} ${primaryButtonEnabled}`}>
                  Next Step
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : currentStep !== 'output' ? (
        <div className="flex-1 mx-[9%] pb-4 min-h-0 flex flex-col">
          <div className="flex gap-4 flex-1 min-h-0">
            {/* Main Content Area - All steps - 65% */}
            <div className="w-[65%] h-full flex flex-col min-h-0">
              {currentStep === WorkflowStep.EQUIPMENT && (
                <div className="glass-panel rounded-3xl h-full flex flex-col min-h-0 border border-emerald-100/60">
                  <div className="flex-1 overflow-y-auto p-8 min-h-0">
                    <EquipmentStep isModuleSelectorEnabled={!!taskData.selected_task} />
                  </div>
                  {/* Navigation buttons inside content */}
                  <div className="flex-shrink-0 border-t border-emerald-100/60 px-8 py-6 bg-white/70 rounded-b-3xl backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handlePreviousStep}
                        className={secondaryButtonClass}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>

                      {!isCurrentStepValid() && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200/60 rounded-lg">
                          <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.832c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <span className="text-[10px] 3xl:text-xs font-medium text-red-600">Complete required module and inverter fields before proceeding</span>
                        </div>
                      )}

                      <button
                        onClick={handleNextStep}
                        className={`${primaryButtonBase} ${!isCurrentStepValid() ? primaryButtonDisabled : primaryButtonEnabled}`}
                      >
                        Next Step
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {currentStep === WorkflowStep.ELECTRICAL && (
                <div className="glass-panel rounded-3xl h-full flex flex-col min-h-0 border border-slate-200/70">
                  <div className="flex-1 overflow-y-auto p-6 min-h-0">
                    <ElectricalStep isEnabled={true} />
                  </div>
                  {/* Navigation buttons inside content */}
                  <div className="flex-shrink-0 border-t border-slate-200/60 px-6 py-4 bg-white/70 rounded-b-3xl">
                    <div className="flex justify-between">
                      <button
                        onClick={handlePreviousStep}
                        className={secondaryButtonClass}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>
                      
                      <button
                        onClick={handleNextStep}
                        className={`${primaryButtonBase} ${primaryButtonEnabled}`}
                      >
                        Next Step
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className={`glass-panel rounded-3xl h-full flex flex-col min-h-0 border border-slate-200/70 ${currentStep !== WorkflowStep.TASK ? 'hidden' : ''}`}>
                <div className="flex-1 overflow-y-auto p-8 pr-6 min-h-0">
                  <SimpleTaskSelector
                    selectedTask={taskData.selected_task}
                    onTaskSelect={handleTaskSelect}
                    isTaskLocked={isResuming || hydratedFromExisting}
                  />
                </div>
                  {/* Navigation buttons fixed at bottom */}
                  <div className="flex-shrink-0 border-t border-slate-200/60 px-8 py-4 bg-white/70 rounded-b-3xl">
                    <div className="flex justify-between">
                      {/* Previous button - only show if not on first step */}
                      {currentStep !== WORKFLOW_STEPS[0].id && (
                        <button
                          onClick={handlePreviousStep}
                          className={secondaryButtonClass}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                          </svg>
                          Previous
                        </button>
                      )}
                      
                      {/* Spacer for first step */}
                      {currentStep === WORKFLOW_STEPS[0].id && <div></div>}
                      
                      <button
                        onClick={handleNextStep}
                        className={`${primaryButtonBase} ${!isCurrentStepValid() ? primaryButtonDisabled : primaryButtonEnabled}`}
                      >
                        Next Step
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
              </div>

              {currentStep === WorkflowStep.MOUNTING && (
                <div className="glass-panel rounded-3xl h-full flex flex-col min-h-0 border border-slate-200/70">
                  <div className="flex-1 overflow-y-auto p-6 min-h-0">
                    <MountingStep isEnabled={isModuleSelectorEnabled} />
                  </div>
                  {/* Navigation buttons inside content */}
                  <div className="flex-shrink-0 border-t border-slate-200/60 px-6 py-4 bg-white/70 rounded-b-3xl">
                    <div className="flex justify-between">
                      <button
                        onClick={handlePreviousStep}
                        className={secondaryButtonClass}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>
                      
                      <button
                        onClick={handleNextStep}
                        className={`${primaryButtonBase} ${primaryButtonEnabled}`}
                      >
                        Next Step
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* BOM step is rendered full-width outside this column */}

              {currentStep === WorkflowStep.DOCUMENTATION && (
                <div className="glass-panel rounded-3xl h-full flex flex-col min-h-0 border border-slate-200/70">
                  <div className="flex-1 overflow-y-auto p-6 min-h-0">
                    <DocumentationStep 
                      isEnabled={isModuleSelectorEnabled}
                      uiState={{
                        necValue: documentationData.nec_value,
                        ibcValue: documentationData.ibc_value,
                        ircValue: documentationData.irc_value,
                        ifcValue: documentationData.ifc_value,
                        ahjValue: documentationData.ahj_value,
                        umNumberValue: documentationData.um_number_value,
                        auroraLinkValue: documentationData.aurora_value,
                        gdriveLinkValue: documentationData.gdrive_link_value,
                        installNotesValue: documentationData.install_notes_value,
                        miscellaneousNotesValue: documentationData.miscellaneous_notes_value,
                        notionCxValue: documentationData.notion_cx_value,
                        notionUxValue: documentationData.notion_ux_value,
                        notionAhjValue: documentationData.notion_ahj_value,
                        bosLocationValue: documentationData.bos_location_value,
                        poiNotesValue: documentationData.poi_notes_value,
                        designNotesValue: documentationData.design_notes_value,
                        lgcyLisValue: documentationData.lgcy_lis_number_value
                      }}
                      onNecChange={(value) => dispatch(updateDocumentationData({ nec_value: value }))}
                      onIbcChange={(value) => dispatch(updateDocumentationData({ ibc_value: value }))}
                      onIrcChange={(value) => dispatch(updateDocumentationData({ irc_value: value }))}
                      onIfcChange={(value) => dispatch(updateDocumentationData({ ifc_value: value }))}
                      onAhjChange={(value) => dispatch(updateDocumentationData({ ahj_value: value }))}
                      onUmNumberChange={(value) => dispatch(updateDocumentationData({ um_number_value: value }))}
                      onAuroraLinkChange={(value) => dispatch(updateDocumentationData({ aurora_value: value }))}
                      onGdriveLinkChange={(value) => dispatch(updateDocumentationData({ gdrive_link_value: value }))}
                      onInstallNotesChange={(value) => dispatch(updateDocumentationData({ install_notes_value: value }))}
                      onMiscellaneousNotesChange={(value) => dispatch(updateDocumentationData({ miscellaneous_notes_value: value }))}
                      onNotionCxChange={(value) => dispatch(updateDocumentationData({ notion_cx_value: value }))}
                      onNotionUxChange={(value) => dispatch(updateDocumentationData({ notion_ux_value: value }))}
                      onNotionAhjChange={(value) => dispatch(updateDocumentationData({ notion_ahj_value: value }))}
                      onBosLocationChange={(value) => dispatch(updateDocumentationData({ bos_location_value: value }))}
                      onPoiNotesChange={(value) => dispatch(updateDocumentationData({ poi_notes_value: value }))}
                      onDesignNotesChange={(value) => dispatch(updateDocumentationData({ design_notes_value: value }))}
                      onLgcyLisChange={(value) => dispatch(updateDocumentationData({ lgcy_lis_number_value: value }))}
                    />
                  </div>
                  {/* Navigation buttons inside content */}
                  <div className="flex-shrink-0 border-t border-slate-200/60 px-6 py-4 bg-white/70 rounded-b-3xl">
                    <div className="flex justify-between">
                      <button
                        onClick={handlePreviousStep}
                        className={secondaryButtonClass}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>
                      
                      <button
                        onClick={handleNextStep}
                        className={`${primaryButtonBase} ${primaryButtonEnabled}`}
                      >
                        Generate CR
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
            </div>

            {/* Right Panel - CR Output (35%) */}
            <div className="w-[35%] h-full min-h-0">
              <div className="rounded-3xl overflow-hidden h-full flex flex-col min-h-0 border border-slate-200/80 bg-white shadow-lg shadow-slate-200/50">
                {/* Header */}
                <div className="flex-shrink-0 px-5 py-3 border-b border-blue-200/70 bg-blue-100/50">
                  <div className="flex items-center justify-center">
                    <div className="flex items-center gap-2.5">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm 3xl:text-base tracking-wide font-semibold text-slate-700">Construction Review Output</span>
                    </div>
                  </div>
                </div>
                
                {/* Output Content */}
                <div className="flex-1 overflow-auto p-4 text-xs min-h-0 bg-white">
                  <OutputPreview 
                    onSaveComplete={handleSaveComplete} 
                    showButtons={false}
                    activeTab={activeTab}
                    onActiveTabChange={(tab) => dispatch(setActiveTab(tab))}
                    crData={crOutput}
                    onCRDataChange={(data) => dispatch(setCROutput(data))}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Step 6: Output Review - Full Width */
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200/50 overflow-hidden">
            {/* ... existing output content ... */}
          </div>
        </div>
      )}


      {/* Output step content */}
      {currentStep === WorkflowStep.OUTPUT && (
        <div className="mx-[9%] pb-6">
          <div className="mb-8">
            <div className="glass-panel rounded-3xl overflow-hidden border border-indigo-100/80">
            <div className="bg-indigo-50/70 px-6 py-4 border-b border-indigo-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <h2 className="text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Construction Review Output</h2>
                    <p className="text-[10px] 3xl:text-[11px] text-slate-500 mt-0.5">Finalize and export with confidence</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleCopy}
                    className="text-xs px-4 py-2 bg-white/80 border border-slate-200 text-slate-700 hover:border-slate-300 font-semibold rounded-full shadow-sm transition-all duration-200"
                    disabled={!crOutput?.updated_cr || Object.keys(crOutput.updated_cr).length === 0}
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy to Clipboard
                  </Button>
                  <Button 
                    size="sm" 
                    variant="primary" 
                    onClick={handleSaveClick}
                    className="px-6 py-2.5 bg-emerald-600 text-white hover:bg-emerald-700 font-bold rounded-full shadow-sm shadow-emerald-500/30 transition-all duration-200 text-sm"
                    disabled={!taskData.selected_task}
                  >
                    <div className="relative flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      <span>Save Final CR</span>
                    </div>
                  </Button>
                </div>
              </div>
            </div>
            <div className="p-8 pb-14 max-h-[70vh] overflow-auto">
              <OutputPreview 
                onSaveComplete={handleSaveComplete} 
                showButtons={false}
                activeTab={activeTab}
                onActiveTabChange={(tab) => dispatch(setActiveTab(tab))}
                crData={crOutput}
                onCRDataChange={(data) => dispatch(setCROutput(data))}
              />
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Mounting Warning Dialog */}
      <ConfirmationModal
        isOpen={!!mountingWarning}
        onClose={() => setMountingWarning(null)}
        onConfirm={handleMountingWarningProceed}
        type={ConfirmationType.WARNING}
        title="Panel Rows Warning"
        message={mountingWarning}
        confirmButtonText="Proceed Anyway"
        cancelButtonText="Go Back"
      />

      {/* BOM Zero Quantity Warning Dialog */}
      <ConfirmationModal
        isOpen={!!bomWarning}
        onClose={() => setBomWarning(null)}
        onConfirm={handleBomWarningProceed}
        type={ConfirmationType.WARNING}
        title="Package Items Warning"
        message={bomWarning}
        confirmButtonText="Proceed Anyway"
        cancelButtonText="Go Back"
      />

      {/* Save Confirmation Dialog */}
      <ConfirmationModal
        isOpen={showSaveConfirmation}
        onClose={handleCancelSave}
        onConfirm={handleSaveDraft}
        type={ConfirmationType.SUCCESS}
        title="Save Construction Review"
        message="Are you sure you want to save this Construction Review?"
        confirmButtonText="Yes, Save CR"
        cancelButtonText="Cancel"
      />

      {/* Existing CR Info Dialog */}
      <ConfirmationModal
        isOpen={existingCR !== null}
        onClose={handleViewExistingCR}
        onConfirm={handleProceedWithExistingCR}
        type={ConfirmationType.INFO}
        title="CR Already Exists"
        message={
          <div className="space-y-3">
            <p>A Construction Review already exists for this task:</p>
            <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-[11px] leading-relaxed font-mono">
              <div><span className="font-semibold">Task:</span> {existingCR?.task_name}</div>
              <div><span className="font-semibold">Last updated:</span> {existingCR ? new Date(existingCR.updated_at).toLocaleString() : ''}</div>
              <div><span className="font-semibold">CR Fields:</span> {existingCR ? Object.keys(existingCR.updated_cr).length : 0}</div>
            </div>
            <p>Proceeding will <strong>update the existing CR</strong> — a new one will not be created.</p>
          </div>
        }
        confirmButtonText="Continue & Update"
        cancelButtonText="View Existing CR"
        customIcon={
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        }
      />
      </div>
    </div>
  );
};
